'use client';

import { GridIcon, MapIcon } from '@/assets/icons';
import BellIcon from '@/assets/icons/bell.svg';
import SettingsIcon from '@/assets/icons/settings.svg';
import NotificationMobileMenu from "@/featured/apps/components/notification/NotificationMobileMenu";
import SettingsMobileMenu from "@/featured/apps/components/settings/SettingsMobileMenu";
import { useIsMobile } from '@/utils/hooks/use-mobile';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { Ratio } from 'lucide-react';
import { createPortal } from 'react-dom';

export default function MobileNav() {
  const router = useRouter();
  const isMobile = useIsMobile();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const currentBasePath = router.asPath.split('#')[0].split('?')[0] || '/dashboard';

  const isActive = (item: (typeof navItems)[0]) => {
    if (router.asPath.includes('#notification')) {
      return item.hash === 'notification';
    }
    if (router.asPath.includes('#settings')) {
      return item.hash === 'settings';
    }
    if (item.href === '/dashboard') {
      return router.pathname.startsWith('/dashboard');
    }
    if (item.label === 'Apps') {
      return router.pathname === '/apps';
    }
    if (item.href) {
      return router.asPath === item.href;
    }
    return false;
  };

  const changeRouteParam = (param: string) => {
    const basePath = router.asPath.split('#')[0].split('?')[0] || '/dashboard';
    if (param) {
      router.push(`${basePath}#${param}`, undefined, { shallow: true });
    } else {
      router.push(basePath, undefined, { shallow: true });
    }
  };

  if (!isMobile) return null;

  const navItems = [
    { icon: <MapIcon className="h-5 w-5" />, label: 'Apps', href: '/apps' },
    { icon: <GridIcon className="h-5 w-5" />, label: 'Grid Maps', href: '/grid-maps' },
    { icon: <Ratio className="h-5 w-5" />, label: 'Dashboard', href: '/dashboard' },
    { icon: <BellIcon className="h-5 w-5" />, label: 'Notification', hash: 'notification' },
    { icon: <SettingsIcon className="h-5 w-5" />, label: 'Settings', hash: 'settings' },
  ];

  const content = (
    <>
      {/* Main Navigation */}
      <nav className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-lg fixed bottom-0 left-0 w-full z-[10000]">
        <div className="flex justify-around max-w-md mx-auto">
          {navItems.map((item, index) => {
            const active = isActive(item);

            const textColor = active ? 'text-brand-500' : 'text-gray-500 dark:text-gray-400';
            const hoverColor = 'hover:text-brand-600 dark:hover:text-brand-400';

            const onClick = () =>
              item.href ? router.push(item.href) : changeRouteParam(item.hash || '');

            return (
              <button
                key={index}
                onClick={onClick}
                className={`flex flex-col items-center justify-center p-3 ${textColor} ${hoverColor} transition-colors duration-200`}
              >
                {item.icon}
                <span className="text-xs mt-1">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Conditional Menus */}
      {router.asPath.includes('#notification') && (
        <NotificationMobileMenu
          isOpen={true}
          onClose={() => changeRouteParam('')}
        />
      )}

      {router.asPath.includes('#settings') && (
        <SettingsMobileMenu
          isOpen={true}
          onClose={() => changeRouteParam('')}
        />
      )}
    </>
  );

  if (!mounted) return null;
  return createPortal(content, document.body);
}
